import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	CompleteGitProviderConnectionDocument,
	type CompleteGitProviderConnectionMutationVariables,
	CreateGitProviderConnectionAuthUrlDocument,
	CreateWorkspaceDocument,
	type CreateWorkspaceInput,
	DeleteGitProviderConnectionDocument,
	DeleteWorkspaceDocument,
	GetGitProviderConnectionDocument,
	GetGitProviderConnectionRepositoriesDocument,
	GetGitProviderRepositoryBranchesDocument,
	GetGitProviderRepositoryCommitsDocument,
	GetWorkspaceDocument,
	GetWorkspaceInfoDocument,
	GetWorkspaceMetricsDocument,
	GetWorkspacesDocument,
	ListGitProviderInstallationsDocument,
	type ListGitProviderInstallationsMutationVariables,
	UpdateWorkspaceDocument,
	type UpdateWorkspaceInput,
} from "@/common/artemis-sdk/graphql";
import type {
	GitProviderBranch,
	GitProviderCommit,
	GitProviderConnection,
	GitProviderRepository,
	Workspace,
} from "../model";
import { useGqlClient } from "../use-gql-client";
import { usePagedList } from "./use-paged-list";

/**
 * Fetches the list of workspaces.
 * Returns the raw node array from the paginated response.
 */
export function useWorkspaces() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["workspaces"],
		queryFn: async () => {
			// The document is paginated for SDK consumers (Port lifts the 50 cap by paging); the UI
			// keeps the historical first page.
			const data = await gqlFetch(GetWorkspacesDocument, { first: 50 });
			return (data.workspaces?.nodes as Workspace[]) ?? [];
		},
	});
}

/**
 * Fetches a single workspace by its ID.
 * Returns the workspace metadata or null if not found.
 */
export function useWorkspaceInfo(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["workspaceInfo", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetWorkspaceInfoDocument, {
				workspaceId: workspaceId,
			});
			return (data.workspace as Workspace) ?? null;
		},
		enabled: !!workspaceId,
	});
}

/**
 * Fetches a single workspace by its ID.
 * Returns the workspace data with all level one fields populated or null if not found.
 */
export function useWorkspace(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["workspace", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetWorkspaceDocument, { workspaceId });
			return (data.workspace as Workspace) ?? null;
		},
	});
}

/**
 * Creates a new workspace. Returns the full mutation object so callers can
 * await mutateAsync and get the new workspace id for navigation.
 */
export function useCreateWorkspace() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateWorkspaceInput) => {
			const data = await gqlFetch(CreateWorkspaceDocument, { input });
			return data.createWorkspace.workspace;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["workspaces"] });
			queryClient.invalidateQueries({ queryKey: ["organizationMetrics"] });
		},
	});
}

/** Updates an existing workspace. Invalidates workspace queries on success. */
export function useUpdateWorkspace(workspaceId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: async (input: UpdateWorkspaceInput) => {
			const data = await gqlFetch(UpdateWorkspaceDocument, {
				workspaceId,
				input,
			});
			return data.updateWorkspace.workspace;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["workspaceInfo", workspaceId],
			});
			queryClient.invalidateQueries({ queryKey: ["workspace", workspaceId] });
			queryClient.invalidateQueries({ queryKey: ["workspaces"] });
		},
	});
	return mutate;
}

/** Returns pre-aggregated metrics for the workspace overview page. */
export function useWorkspaceMetrics(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["workspaceMetrics", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetWorkspaceMetricsDocument, { workspaceId });
			return data.workspaceMetrics;
		},
	});
}

/** Deletes a workspace. Invalidates the workspaces list on success. */
export function useDeleteWorkspace(workspaceId?: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		// id can be passed per-call (for list views) or bound at hook time (for a single workspace).
		mutationFn: async (id?: string) => {
			const data = await gqlFetch(DeleteWorkspaceDocument, {
				workspaceId: id ?? (workspaceId as string),
			});
			return data.deleteWorkspace.success;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["workspaces"] });
		},
	});
	return mutate;
}

// ─── Git provider connection (REQ-090) ─────────────────────────────────────────

/** The organization's git provider connection, or null if none is configured (ADR-062: one per organization). */
export function useGitProviderConnection() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["gitProviderConnection"],
		queryFn: async () => {
			const data = await gqlFetch(GetGitProviderConnectionDocument);
			return (data.gitProviderConnection as GitProviderConnection) ?? null;
		},
	});
}

/** Builds the GitHub sign-in URL (`url`) and App installation URL (`installUrl`) to send the browser
 * to; GitHub returns to the backend's callback, which redirects back to Admin → Apps. Org admin only. */
export function useCreateGitProviderConnectionAuthUrl() {
	const gqlFetch = useGqlClient();
	return useMutation({
		mutationFn: async () =>
			(await gqlFetch(CreateGitProviderConnectionAuthUrlDocument))
				.createGitProviderConnectionAuthUrl,
	});
}

/**
 * Lists the GitHub App installations the signed-in org admin can connect the organization to, from
 * the parameters GitHub put on the callback redirect. The server only accepts a state minted for the
 * signed-in user, once (SEC-002 F1).
 */
export function useListGitProviderInstallations() {
	const gqlFetch = useGqlClient();
	return useMutation({
		mutationFn: async (
			variables: ListGitProviderInstallationsMutationVariables,
		) => {
			const data = await gqlFetch(
				ListGitProviderInstallationsDocument,
				variables,
			);
			return data.listGitProviderInstallations;
		},
	});
}

/**
 * Connects the organization to an installation picked from `useListGitProviderInstallations`, using
 * the grant the server issued for it. One installation per organization (ADR-062).
 */
export function useCompleteGitProviderConnection() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (
			variables: CompleteGitProviderConnectionMutationVariables,
		) => {
			const data = await gqlFetch(
				CompleteGitProviderConnectionDocument,
				variables,
			);
			return data.completeGitProviderConnection;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["gitProviderConnection"] });
			queryClient.invalidateQueries({ queryKey: ["organizationApps"] });
		},
	});
}

/** Disconnects the organization's git provider connection. Org admin only. */
export function useDeleteGitProviderConnection() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (connectionId: string) => {
			const data = await gqlFetch(DeleteGitProviderConnectionDocument, {
				connectionId,
			});
			return data.deleteGitProviderConnection.success;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["gitProviderConnection"] });
			queryClient.invalidateQueries({ queryKey: ["organizationApps"] });
		},
	});
}

/**
 * Server-side paginated browse of repositories the workspace's connected installation can
 * access. Unlike every other paged list here, the API isn't relay-style: it returns a flat
 * `{ repositories, nextCursor }` page with a single forward-only cursor, so only `window.after`
 * is used to drive it and there is no previous page.
 */
export function useGitProviderConnectionRepositories(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return usePagedList<GitProviderRepository>({
		baseKey: ["gitProviderConnectionRepositories", workspaceId],
		enabled: !!workspaceId,
		// This calls a live, rate-limited external API (SEC-002 F10) — refetching on every window
		// focus while the browse modal happens to be open gives nothing (repo lists don't change
		// second to second) and just spends budget. 30s is long enough to absorb refocus/rerender
		// churn while still feeling live within one browsing session.
		staleTime: 30_000,
		refetchOnWindowFocus: false,
		fetchPage: async ({ debouncedSearch, window }) => {
			const data = await gqlFetch(
				GetGitProviderConnectionRepositoriesDocument,
				{
					workspaceId,
					cursor: window.after ?? undefined,
				},
			);
			const page = data.gitProviderConnectionRepositories;
			const q = debouncedSearch.trim().toLowerCase();
			// ponytail: the provider API has no server-side search param — this filters only the
			// page already fetched, not the whole installation. Add real server search if browsing
			// large installations one page at a time proves painful.
			const repositories = (page?.repositories ?? []).filter(
				(r) => !q || r.fullName.toLowerCase().includes(q),
			);
			return {
				nodes: repositories,
				totalCount: repositories.length,
				pageInfo: {
					hasNextPage: !!page?.nextCursor,
					hasPreviousPage: false,
					endCursor: page?.nextCursor ?? null,
				},
			};
		},
	});
}

/**
 * Server-side paginated browse of a linked repository's recent commits (newest first), addressed
 * by the project repository link — never by name. Same flat `{ commits, nextCursor }` shape and
 * SEC-002 F10 caching rationale as {@link useGitProviderConnectionRepositories}.
 */
export function useGitProviderRepositoryCommits(projectRepositoryId: string) {
	const gqlFetch = useGqlClient();
	return usePagedList<GitProviderCommit>({
		baseKey: ["gitProviderRepositoryCommits", projectRepositoryId],
		enabled: !!projectRepositoryId,
		staleTime: 30_000,
		refetchOnWindowFocus: false,
		fetchPage: async ({ window }) => {
			const data = await gqlFetch(GetGitProviderRepositoryCommitsDocument, {
				projectRepositoryId,
				cursor: window.after ?? undefined,
			});
			const page = data.gitProviderRepositoryCommits;
			const commits = page?.commits ?? [];
			return {
				nodes: commits,
				totalCount: commits.length,
				pageInfo: {
					hasNextPage: !!page?.nextCursor,
					hasPreviousPage: false,
					endCursor: page?.nextCursor ?? null,
				},
			};
		},
	});
}

/**
 * Server-side paginated browse of a linked repository's branches, addressed by the project
 * repository link. The server marks the default branch from the link it stored. Same flat
 * `{ branches, nextCursor }` shape and SEC-002 F10 caching rationale as
 * {@link useGitProviderConnectionRepositories}.
 */
export function useGitProviderRepositoryBranches(projectRepositoryId: string) {
	const gqlFetch = useGqlClient();
	return usePagedList<GitProviderBranch>({
		baseKey: ["gitProviderRepositoryBranches", projectRepositoryId],
		enabled: !!projectRepositoryId,
		staleTime: 30_000,
		refetchOnWindowFocus: false,
		fetchPage: async ({ window }) => {
			const data = await gqlFetch(GetGitProviderRepositoryBranchesDocument, {
				projectRepositoryId,
				cursor: window.after ?? undefined,
			});
			const page = data.gitProviderRepositoryBranches;
			const branches = page?.branches ?? [];
			return {
				nodes: branches,
				totalCount: branches.length,
				pageInfo: {
					hasNextPage: !!page?.nextCursor,
					hasPreviousPage: false,
					endCursor: page?.nextCursor ?? null,
				},
			};
		},
	});
}
